/**
 * WatchRunner —— 单 view 的运行时（M2 简化版）。
 *
 * 设计：
 *   1. **isomorphic**：构造期接收一个 WatchView；不持有任何 Node-only API。
 *   2. **增量喂入**：每收到一行 LogcatLineView 调 feed(line)；按 watcher
 *      逐一评估，命中即 push 到 history（最多 historyCap 条，环形丢弃）
 *      并 emit `hit` 事件。
 *   3. **零判定**：没有 verdict 状态机；watcher 之间互不影响；UI 看的是
 *      "最新值 / 命中次数"。
 *   4. **regex 编译缓存**：跟 checkpoint 同款 symbol-keyed 缓存。
 *   5. **M2 砍简**：移除 match 模式与 view-level 过滤（appPackage / tag /
 *      level）。每个 watcher 是一个 extract，pattern 由前端 `patternFromKey`
 *      构造。
 */

import type {
  ExtractWatcher,
  LogcatLineView,
  WatchEvent,
  WatcherSnapshot,
  WatchHit,
  WatchView,
  WatchViewSnapshot,
} from './types';

export type WatchEventListener = (ev: WatchEvent) => void;

interface WatcherStateInternal {
  watcher: ExtractWatcher;
  history: WatchHit[];
  count: number;
  latest?: WatchHit;
}

export interface WatchRunnerOptions {
  runId: string;
  view: WatchView;
  startedAt?: number;
}

const DEFAULT_HISTORY_CAP = 50;

const RE_CACHE = Symbol('compiledRegex');
interface RegexHolder {
  [RE_CACHE]?: RegExp | { error: string };
}

function compilePattern(
  pattern: string,
  ci: boolean | undefined,
  holder: RegexHolder,
): RegExp | { error: string } {
  if (holder[RE_CACHE]) return holder[RE_CACHE]!;
  try {
    const re = new RegExp(pattern, ci ? 'i' : undefined);
    holder[RE_CACHE] = re;
    return re;
  } catch (e) {
    holder[RE_CACHE] = { error: e instanceof Error ? e.message : String(e) };
    return holder[RE_CACHE]!;
  }
}

function buildHit(opts: { watcherId: string; line: LogcatLineView; value: string }): WatchHit {
  const { line } = opts;
  return {
    watcherId: opts.watcherId,
    lineId: line.lineId,
    tsMs: line.tsMs,
    ts: line.ts,
    pid: line.pid || undefined,
    tid: line.tid || undefined,
    tag: line.tag,
    level: line.level,
    message: line.msg || line.raw,
    value: opts.value,
  };
}

export class WatchRunner {
  private readonly runId: string;
  private readonly view: WatchView;
  private readonly listeners = new Set<WatchEventListener>();
  private readonly states: WatcherStateInternal[];
  private readonly historyCap: number;
  private readonly startedAt: number;
  private finishedAt?: number;
  private linesFed = 0;
  private finished = false;

  constructor(opts: WatchRunnerOptions) {
    this.runId = opts.runId;
    this.view = opts.view;
    this.startedAt = opts.startedAt ?? Date.now();
    this.historyCap = Math.max(1, opts.view.historyCap ?? DEFAULT_HISTORY_CAP);
    this.states = opts.view.watchers.map((watcher) => ({
      watcher,
      history: [],
      count: 0,
    }));
  }

  on(listener: WatchEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getRunId(): string {
    return this.runId;
  }

  getViewId(): string {
    return this.view.id;
  }

  isFinished(): boolean {
    return this.finished;
  }

  /** 喂一行日志；线程不安全，调用方需保证串行。 */
  feed(line: LogcatLineView): void {
    if (this.finished) return;
    this.linesFed += 1;
    for (const st of this.states) {
      if (st.watcher.disabled) continue;
      this.evaluateExtract(st, line);
    }
  }

  finish(nowMs?: number): void {
    if (this.finished) return;
    this.finishedAt = nowMs ?? Date.now();
    this.finished = true;
    this.emit({
      type: 'finished',
      runId: this.runId,
      viewId: this.view.id,
      snapshot: this.snapshot(),
    });
  }

  snapshot(): WatchViewSnapshot {
    return {
      runId: this.runId,
      viewId: this.view.id,
      viewName: this.view.name,
      linesFed: this.linesFed,
      startedAt: this.startedAt,
      finishedAt: this.finishedAt,
      running: !this.finished,
      watchers: this.states.map((st) => this.toWatcherSnapshot(st)),
    };
  }

  private toWatcherSnapshot(st: WatcherStateInternal): WatcherSnapshot {
    return {
      watcherId: st.watcher.id,
      watcherName: st.watcher.name ?? st.watcher.id,
      kind: st.watcher.kind,
      count: st.count,
      history: st.history.slice(),
      latest: st.latest,
      disabled: !!st.watcher.disabled,
    };
  }

  private evaluateExtract(st: WatcherStateInternal, line: LogcatLineView): void {
    const w = st.watcher;
    const compiled = compilePattern(w.pattern, w.caseInsensitive, w as unknown as RegexHolder);
    if ('error' in compiled) return;
    const m = compiled.exec(line.msg) || compiled.exec(line.raw);
    if (!m) return;
    const v = m[1];
    if (typeof v !== 'string') return;
    this.recordHit(st, buildHit({ watcherId: w.id, line, value: v }));
  }

  private recordHit(st: WatcherStateInternal, hit: WatchHit): void {
    st.count += 1;
    st.history.push(hit);
    if (st.history.length > this.historyCap) {
      st.history.splice(0, st.history.length - this.historyCap);
    }
    st.latest = hit;
    this.emit({ type: 'hit', runId: this.runId, viewId: this.view.id, hit });
  }

  private emit(ev: WatchEvent): void {
    for (const l of this.listeners) {
      try {
        l(ev);
      } catch {
        /* noop */
      }
    }
  }
}

/* -------------------- WatchRunnerSet —— 多 view 并行容器 -------------------- */

export interface WatchRunnerSetAddOptions {
  runId: string;
  view: WatchView;
  startedAt?: number;
}

export class WatchRunnerSet {
  private readonly runners = new Map<string, WatchRunner>();
  private readonly listeners = new Set<WatchEventListener>();
  private readonly paused = new Set<string>();
  private nextLineId = 0;

  on(listener: WatchEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  add(opts: WatchRunnerSetAddOptions): WatchRunner {
    if (this.runners.has(opts.runId)) {
      throw new Error(`WatchRunnerSet: duplicate runId ${opts.runId}`);
    }
    const runner = new WatchRunner({
      runId: opts.runId,
      view: opts.view,
      startedAt: opts.startedAt,
    });
    runner.on((ev) => {
      for (const l of this.listeners) {
        try {
          l(ev);
        } catch {
          /* noop */
        }
      }
    });
    this.runners.set(opts.runId, runner);
    return runner;
  }

  remove(runId: string): void {
    const r = this.runners.get(runId);
    if (!r) return;
    if (!r.isFinished()) r.finish();
    this.runners.delete(runId);
    this.paused.delete(runId);
  }

  has(runId: string): boolean {
    return this.runners.has(runId);
  }

  list(): WatchRunner[] {
    return Array.from(this.runners.values());
  }

  pause(runId: string): void {
    if (this.runners.has(runId)) this.paused.add(runId);
  }

  resume(runId: string): void {
    this.paused.delete(runId);
  }

  isPaused(runId: string): boolean {
    return this.paused.has(runId);
  }

  /**
   * 喂入一行；自动给 lineId 编号（如未提供）。多 view 共享同一行 lineId，
   * 方便 SPA 用 hitsAt 跨 view 聚合。
   */
  feed(line: LogcatLineView | Omit<LogcatLineView, 'lineId'>): LogcatLineView {
    const withId: LogcatLineView =
      'lineId' in line && typeof (line as LogcatLineView).lineId === 'number'
        ? (line as LogcatLineView)
        : ({ ...(line as Omit<LogcatLineView, 'lineId'>), lineId: this.nextLineId++ } as LogcatLineView);
    if (withId.lineId >= this.nextLineId) this.nextLineId = withId.lineId + 1;
    for (const [id, r] of this.runners.entries()) {
      if (this.paused.has(id)) continue;
      r.feed(withId);
    }
    return withId;
  }

  finishAll(nowMs?: number): void {
    for (const r of this.runners.values()) r.finish(nowMs);
  }

  snapshot(): WatchViewSnapshot[] {
    return Array.from(this.runners.values(), (r) => r.snapshot());
  }

  getNextLineId(): number {
    return this.nextLineId;
  }
}
